import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { usePreviewKnowledgeBaseLetterMutation } from "knowledge-base/_api/knowledge-base-api";
import ArticleMarkdown from "knowledge-base/articles/_views/article-markdown";
import KnowledgeBaseArticle from "knowledge-base/knowledge-base-article";
import { useEffect, useState } from "react";

/**
 * Sample values, so the preview reads like a real letter rather than a form.
 *
 * Only used to fill the editor's inputs. Anything the author clears renders blank, which is the
 * whole point — a blank in the preview is a blank in the letter.
 */
const SAMPLE_VALUES: Record<string, string> = {
  ClientName: "Northwind Motors",
  FirmName: "Baker & Reid LLP",
  TeamLeaderName: "Alex Moreau",
  TeamMemberName: "Sam Okafor",
  InvitationLink: "https://attorneyhub.example/invitations/sample",
  ProposalLink: "https://attorneyhub.example/proposals/sample",
  WorkAgreementLink: "https://attorneyhub.example/work/sample",
  SupportContactName: "Antun Peakovic",
  SupportContactTitle: "CEO",
  SupportContactEmail: "apeakovic@attorneyhub.com",
  SupportContactPhone: "(000) 000-0000",
  SupportContactProfileLink: "https://attorneyhub.example/profiles/sample",
};

type Props = Readonly<{ article: KnowledgeBaseArticle }>;

export default function LetterPreview({ article }: Props) {
  const [preview, { data, isLoading }] = usePreviewKnowledgeBaseLetterMutation();
  // Seeded rather than set from the effect: the samples are known before the first render, and
  // setting them in an effect would cost a cascading render for no gain.
  const [values, setValues] = useState<Record<string, string>>(SAMPLE_VALUES);

  // Preview the saved article. Unsaved edits are deliberately not previewed: what this answers is
  // "what will a recipient get", and the answer for unsaved text is "nothing, yet".
  useEffect(() => {
    preview({ articleId: article.id, placeholders: SAMPLE_VALUES });
  }, [article.id, preview]);

  const rerender = (next: Record<string, string>) => {
    setValues(next);
    preview({ articleId: article.id, placeholders: next });
  };

  if (isLoading && !data) {
    return <Typography color="text.secondary">Rendering the letter…</Typography>;
  }

  if (!data) {
    return <Alert severity="error">The letter could not be previewed.</Alert>;
  }

  return (
    <Stack spacing={3}>
      {data.unsuppliedPlaceholders.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>These would arrive blank</AlertTitle>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mt: 1 }}>
            {data.unsuppliedPlaceholders.map((placeholder) => (
              <Chip key={placeholder} label={`{{${placeholder}}}`} size="small" />
            ))}
          </Stack>
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: 1, borderColor: "divider", p: 3 }}>
        <Typography color="text.secondary" variant="overline">
          Subject
        </Typography>
        <Typography gutterBottom variant="h6">
          {data.subject}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <ArticleMarkdown>{data.body}</ArticleMarkdown>
      </Paper>

      <Box>
        <Typography gutterBottom variant="subtitle2">
          Sample values
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          These stand in for what the application supplies when it sends this letter. Clear one to
          see what a recipient gets when that value is missing.
        </Typography>
        <Stack spacing={1.5}>
          {data.declaredPlaceholders.map((placeholder) => (
            <TextField
              key={placeholder}
              label={`{{${placeholder}}}`}
              onChange={(event) => rerender({ ...values, [placeholder]: event.target.value })}
              size="small"
              value={values[placeholder] ?? ""}
            />
          ))}
        </Stack>
        <Button
          onClick={() => rerender(SAMPLE_VALUES)}
          size="small"
          startIcon={<RestartAltIcon />}
          sx={{ mt: 2 }}
        >
          Reset to samples
        </Button>
      </Box>
    </Stack>
  );
}
